import { FC, useState } from 'react';

import { useForm } from '@inertiajs/react';
import { ChevronDown, ChevronUp, Trash2 } from 'lucide-react';

import { Button } from '../../components/ui/Button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../../components/ui/Card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/Dialog';
import { Routes } from '../../models/routes';

interface Props {
    isOpen: boolean;
    onToggle: () => void;
}

export const DeleteProfileForm: FC<Props> = ({ isOpen, onToggle }) => {
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const { delete: deleteProfile, processing } = useForm({});

    const handleDelete = () => {
        deleteProfile(Routes.ProfileAccount);
    };

    return (
        <>
            <Card
                className="h-fit border-destructive/40"
                data-testid="delete-profile-form"
            >
                <CardHeader
                    className="cursor-pointer transition-colors hover:bg-muted/50"
                    onClick={onToggle}
                >
                    <CardTitle className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Trash2 className="h-5 w-5 text-destructive" />
                            Видалення профілю
                        </div>
                        {isOpen ? (
                            <ChevronUp className="h-4 w-4 text-muted-foreground" />
                        ) : (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        )}
                    </CardTitle>
                    <CardDescription>
                        Видаліть свій профіль і пропозиції
                    </CardDescription>
                </CardHeader>

                {isOpen && (
                    <CardContent className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Після видалення профіль і всі ваші пропозиції
                            стануть недоступними.
                        </p>
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={() => setConfirmationOpen(true)}
                        >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Видалити профіль
                        </Button>
                    </CardContent>
                )}
            </Card>

            <Dialog
                open={confirmationOpen}
                onOpenChange={(open) => {
                    if (!processing) setConfirmationOpen(open);
                }}
            >
                <DialogContent className="w-[calc(100%-2rem)] sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Видалити профіль?</DialogTitle>
                        <DialogDescription>
                            Після видалення ви вийдете з облікового запису, а
                            профіль і ваші пропозиції більше не будуть доступні.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:space-x-0">
                        <Button
                            type="button"
                            variant="outline"
                            disabled={processing}
                            onClick={() => setConfirmationOpen(false)}
                        >
                            Скасувати
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            disabled={processing}
                            onClick={handleDelete}
                        >
                            {processing
                                ? 'Видалення…'
                                : 'Так, видалити профіль'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
